/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

/**
 * @deprecated
 * This is the v1 admin consent modal, driven by the `usePermission` hook.
 *
 * The v2 equivalent is:
 *   `platform/packages/forge/forge-ui/src/bridge/methods/permissions/AdminConsentModal.tsx`
 *
 * The `styles`, `messages`, and `useConditionalIntl` exports from this file are
 * intentionally re-used by the v2 modal to keep the UI consistent while avoiding
 * duplication. Do not remove those exports.
 *
 * Do not add new UI logic here — add it to the v2 modal instead.
 */

import { jsx, type CompiledStyles } from '@compiled/react';

import type { ModalState } from './usePermission';

import ModalDialog, {
	ModalTransition,
	ModalFooter as AKModalFooter,
	ModalBody,
	ModalTitle,
	ModalHeader,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { functionWithFG } from '@atlaskit/platform-feature-flags-react';
import { Box, Text, Stack, Inline, Flex } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-invalid-css-map -- Shared with v2 bridge modal for style consistency. See go/DSP-18766
export const styles: {
    readonly titleContainer: CompiledStyles<{
        marginBottom: "var(--ds-space-400)";
    }>; readonly modalFooterContainer: CompiledStyles<{
        borderTopStyle: "solid";
        borderTopWidth: "var(--ds-border-width)";
        borderTopColor: "var(--ds-border)";
    }>; readonly disclaimerContainer: CompiledStyles<{
        marginTop: "var(--ds-space-400)";
        marginBottom: "var(--ds-space-400)";
    }>; readonly rowContainer: CompiledStyles<{
        paddingTop: "var(--ds-space-100)";
        paddingBottom: "var(--ds-space-100)";
        paddingRight: "var(--ds-space-100)";
        paddingLeft: "var(--ds-space-100)";
        borderBottomStyle: "solid";
        borderBottomWidth: "var(--ds-border-width)";
        borderBottomColor: "var(--ds-border)";
    }>;
} = cssMap({
	titleContainer: {
		marginBottom: token('space.400'),
	},
	modalFooterContainer: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
	disclaimerContainer: {
		marginTop: token('space.400'),
		marginBottom: token('space.400'),
	},
	rowContainer: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
	},
});

export const messages: {
    declineButtonLabel: {
        id: string;
        defaultMessage: string;
        description: string;
    }; approveButtonLabel: {
        id: string;
        defaultMessage: string;
        description: string;
    }; noticeForMoreDetails: {
        id: string;
        defaultMessage: string;
        description: string;
    }; noticeAnd: {
        id: string;
        defaultMessage: string;
        description: string;
    }; termsOfUseLink: {
        id: string;
        defaultMessage: string;
        description: string;
    }; privacyPolicyLink: {
        id: string;
        defaultMessage: string;
        description: string;
    }; modalTransitionTitle: {
        id: string;
        defaultMessage: string;
        description: string;
    }; modalTransitionText: {
        id: string;
        defaultMessage: string;
        description: string;
    };
} = defineMessages({
	declineButtonLabel: {
		id: 'forge-ui.adminConsentModal.decline.button.label',
		defaultMessage: 'Decline',
		description:
			'Text label on the decline button in the admin consent modal, which rejects the app permission request.',
	},
	approveButtonLabel: {
		id: 'forge-ui.adminConsentModal.approve.button.label',
		defaultMessage: 'Approve',
		description:
			'Text label on the approve button in the admin consent modal, which grants the app the requested permissions.',
	},
	noticeForMoreDetails: {
		id: 'forge-ui.adminConsentModal.notice.for-more-details',
		defaultMessage: "For more details, refer to {appName}'s",
		description:
			'First part of the notice text in the admin consent modal. The placeholder {appName} is substituted with the name of the Forge app requesting permissions.',
	},
	noticeAnd: {
		id: 'forge-ui.adminConsentModal.notice.and',
		defaultMessage: 'and',
		description:
			'Conjunction word displayed between the terms of use and privacy policy links in the admin consent modal notice.',
	},
	termsOfUseLink: {
		id: 'forge-ui.adminConsentModal.terms-of-use.link',
		defaultMessage: 'terms of use',
		description:
			'Clickable link text in the admin consent modal notice that navigates the user to the app terms of use page.',
	},
	privacyPolicyLink: {
		id: 'forge-ui.adminConsentModal.privacy-policy.link',
		defaultMessage: 'privacy policy',
		description:
			'Clickable link text in the admin consent modal notice that navigates the user to the app privacy policy page.',
	},
	modalTransitionTitle: {
		id: 'forge-ui.adminConsentModal.modal-transition.title',
		defaultMessage: 'Data storage, sharing, receiving and processing',
		description:
			'Heading displayed at the top of the admin consent modal, summarizing the data operations the app is requesting permission for.',
	},
	modalTransitionText: {
		id: 'forge-ui.adminConsentModal.modal-transition.text',
		defaultMessage:
			'{appName} is requesting to share with and receive data from the following domains:',
		description:
			'Body text in the admin consent modal explaining what the app wants to do. The placeholder {appName} is substituted with the name of the Forge app requesting data access.',
	},
});

export const useConditionalIntl: () => any = functionWithFG<() => any>(
	'forge-ui-add-use-intl-to-format-messages',
	useIntl,
	() => ({
		formatMessage: (_: any, valuesOrFallback?: any, fallback?: string): string => {
			if (typeof valuesOrFallback === 'string') {
				return valuesOrFallback;
			}
			return fallback ?? '';
		},
	}),
);

export const AdminModal = ({ modalState }: { modalState: ModalState }): JSX.Element => {
	const intl = useConditionalIntl();

	if (modalState.state === 'closed') {
		return <ModalTransition />;
	}

	const ModalFooter = () => {
		return (
			<Box xcss={styles.modalFooterContainer}>
				<AKModalFooter>
					<Inline space="space.075">
						<Button onClick={modalState.onCancel}>
							{intl.formatMessage(messages.declineButtonLabel, 'Decline')}
						</Button>
						<Button onClick={modalState.onConfirm} appearance="primary">
							{intl.formatMessage(messages.approveButtonLabel, 'Approve')}
						</Button>
					</Inline>
				</AKModalFooter>
			</Box>
		);
	};

	const renderDomains = () => {
		const getHumanReadableName = (operation: string) => {
			switch (operation) {
				case 'FETCH_BACKEND_SIDE':
					return 'Fetch backend side';
				case 'FETCH_CLIENT_SIDE':
					return 'Fetch client side';
				case 'FONTS':
					return 'Fonts';
				case 'FRAMES':
					return 'Frames';
				case 'IMAGES':
					return 'Images';
				case 'MEDIA':
					return 'Media';
				case 'NAVIGATION':
					return 'Navigation';
				case 'SCRIPTS':
					return 'Scripts';
				case 'STYLES':
					return 'Styles';
				default:
					return 'Egress';
			}
		};

		const domainInfo = modalState.domainInfo;

		if (Object.keys(domainInfo).length === 0) {
			return null;
		}

		if (domainInfo.type === 'REMOTE') {
			return (
				<Flex direction="column" gap="space.150">
					{domainInfo.payload.remotes.map((r) => {
						const manifestRemoteInfo = domainInfo.manifestRemoteMap[r.key];

						return (
							<Stack key={r.key} space="space.150">
								<Heading size="small">
									{manifestRemoteInfo ? manifestRemoteInfo.description : 'Remote'}
								</Heading>
								<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
									<Text>
										{r.configured.endpoint === null
											? 'This remote is being reset to the default value'
											: r.configured.endpoint}
									</Text>
									<Text>{manifestRemoteInfo ? manifestRemoteInfo.name : null}</Text>
								</Inline>
							</Stack>
						);
					})}
				</Flex>
			);
		}

		if (domainInfo.type === 'EGRESS') {
			return (
				<Flex direction="column" gap="space.150">
					{domainInfo.payload.egresses.map((e) => (
						<Stack key={e.key} space="space.150">
							<Heading size="xsmall">{e.description}</Heading>
							{e.configured.map((c) => {
								return c.type.map((t) => {
									return (
										<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
											<Text>{c.domain}</Text>
											<Text>{getHumanReadableName(t)}</Text>
										</Inline>
									);
								});
							})}
						</Stack>
					))}
				</Flex>
			);
		}
	};

	const Notice = () => {
		const { appName, termsOfUseLink, privacyPolicyLink } = modalState;
		if (termsOfUseLink && privacyPolicyLink) {
			return (
				<Text>
					{intl!.formatMessage(
						messages.noticeForMoreDetails,
						{ appName: appName ?? 'the app' },
						`For more details, refer to ${appName}\'s`,
					)}{' '}
					<Link href={termsOfUseLink}>
						{intl!.formatMessage(messages.termsOfUseLink, 'terms of use')}
					</Link>{' '}
					{intl!.formatMessage(messages.noticeAnd, 'and')}{' '}
					<Link href={privacyPolicyLink}>
						{intl!.formatMessage(messages.privacyPolicyLink, 'privacy policy')}
					</Link>
				</Text>
			);
		} else if (termsOfUseLink) {
			return (
				<Text>
					{intl!.formatMessage(
						messages.noticeForMoreDetails,
						{ appName: appName ?? 'the app' },
						`For more details, refer to ${appName}\'s`,
					)}{' '}
					<Link href={termsOfUseLink}>
						{intl!.formatMessage(messages.termsOfUseLink, 'terms of use')}
					</Link>
				</Text>
			);
		} else if (privacyPolicyLink) {
			return (
				<Text>
					{intl!.formatMessage(
						messages.noticeForMoreDetails,
						{ appName: appName ?? 'the app' },
						`For more details, refer to ${appName}\'s`,
					)}{' '}
					<Link href={privacyPolicyLink}>
						{intl!.formatMessage(messages.privacyPolicyLink, 'privacy policy')}
					</Link>
				</Text>
			);
		}
	};

	return (
		<ModalTransition>
			<ModalDialog
				onClose={() => {
					modalState.onCancel();
				}}
				width="large"
			>
				<ModalHeader>
					<Box xcss={styles.titleContainer}>
						<ModalTitle>
							{intl!.formatMessage(
								messages.modalTransitionTitle,
								'Data storage, sharing, receiving and processing',
							)}
						</ModalTitle>
					</Box>
				</ModalHeader>
				<ModalBody>
					<Stack space="space.300">
						<Text>
							{intl!.formatMessage(
								messages.modalTransitionText,
								{ appName: modalState.appName ?? 'The app' },
								`${modalState.appName ?? 'The app'
								} is requesting to share with and receive data from the following domains:`,
							)}
						</Text>
						<Box>
							{renderDomains()}
							<Box xcss={styles.disclaimerContainer}>
								<Notice />
							</Box>
						</Box>
					</Stack>
				</ModalBody>
				<ModalFooter />
			</ModalDialog>
		</ModalTransition>
	);
};
